import { useState, useEffect, useCallback } from "react";
import {
  getFolders,
  getFolderChildren,
  getListsInFolder,
  getListById,
  isDashpivotConfigured,
  getDashpivotConfigStatus,
  getLocationsListId,
  type DashpivotFolder,
  type DashpivotList,
  type DashpivotListItem,
} from "../lib/sitemate";
import { logError } from "../lib/logError";

// ============================================
// Hook: Check Dashpivot Configuration
// ============================================

export function useDashpivotConfig() {
  const status = getDashpivotConfigStatus();

  return {
    isConfigured: status.configured,
    status,
  };
}

// ============================================
// Hook: Fetch Dashpivot Folders
// ============================================

interface UseDashpivotFoldersResult {
  folders: DashpivotFolder[];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export function useDashpivotFolders(): UseDashpivotFoldersResult {
  const [folders, setFolders] = useState<DashpivotFolder[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchFolders = useCallback(async () => {
    if (!isDashpivotConfigured()) {
      setError("Dashpivot API not configured");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- auto-suppressed for safety
      const response = await getFolders({ state: "active" } as any);
      setFolders(response.data);
    } catch (err) {
      logError("Error fetching Dashpivot folders:", err);
      setError(err instanceof Error ? err.message : "Failed to fetch folders");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchFolders();
  }, [fetchFolders]);

  return { folders, loading, error, refetch: fetchFolders };
}

// ============================================
// Hook: Fetch Folder Children (Projects)
// ============================================

interface UseDashpivotProjectsResult {
  projects: DashpivotFolder[];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export function useDashpivotProjects(
  folderId: string | null
): UseDashpivotProjectsResult {
  const [projects, setProjects] = useState<DashpivotFolder[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchProjects = useCallback(async () => {
    if (!folderId || !isDashpivotConfigured()) {
      setProjects([]);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await getFolderChildren(folderId, { state: "active" });
      setProjects(response.data);
    } catch (err) {
      logError("Error fetching Dashpivot projects:", err);
      setError(err instanceof Error ? err.message : "Failed to fetch projects");
    } finally {
      setLoading(false);
    }
  }, [folderId]);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  return { projects, loading, error, refetch: fetchProjects };
}

// ============================================
// Hook: Fetch Lists in a Folder
// ============================================

interface UseDashpivotListsResult {
  lists: DashpivotList[];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export function useDashpivotLists(
  folderId: string | null
): UseDashpivotListsResult {
  const [lists, setLists] = useState<DashpivotList[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchLists = useCallback(async () => {
    if (!folderId || !isDashpivotConfigured()) {
      setLists([]);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await getListsInFolder(folderId, { state: "active" });
      setLists(response.data);
    } catch (err) {
      logError("Error fetching Dashpivot lists:", err);
      setError(err instanceof Error ? err.message : "Failed to fetch lists");
    } finally {
      setLoading(false);
    }
  }, [folderId]);

  useEffect(() => {
    fetchLists();
  }, [fetchLists]);

  return { lists, loading, error, refetch: fetchLists };
}

// ============================================
// Hook: Fetch List Items
// ============================================

interface UseDashpivotListItemsResult {
  items: DashpivotListItem[];
  listName: string | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export function useDashpivotListItems(
  listId: string | null
): UseDashpivotListItemsResult {
  const [items, setItems] = useState<DashpivotListItem[]>([]);
  const [listName, setListName] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchItems = useCallback(async () => {
    if (!listId || !isDashpivotConfigured()) {
      setItems([]);
      setListName(null);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await getListById(listId);
      setItems(response.data.items || []);
      setListName(response.data.name);
    } catch (err) {
      logError("Error fetching Dashpivot list items:", err);
      setError(
        err instanceof Error ? err.message : "Failed to fetch list items"
      );
    } finally {
      setLoading(false);
    }
  }, [listId]);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  return { items, listName, loading, error, refetch: fetchItems };
}

// ============================================
// Hook: Combined - Fetch Project Locations
// ============================================

interface ProjectLocation {
  id: string;
  name: string;
  source: "dashpivot" | "manual";
}

interface UseProjectLocationsResult {
  locations: ProjectLocation[];
  loading: boolean;
  error: string | null;
  isConfigured: boolean;
  refetch: () => Promise<void>;
}

/**
 * Hook to fetch project locations from Dashpivot list
 * Falls back gracefully if Dashpivot is not configured
 */
export function useProjectLocations(
  excludeNames: string[] = []
): UseProjectLocationsResult {
  const [locations, setLocations] = useState<ProjectLocation[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const isConfigured = isDashpivotConfigured();

  const fetchLocations = useCallback(async () => {
    if (!isConfigured) {
      setLocations([]);
      return;
    }

    // Get the list ID from environment
    const listId = getLocationsListId();

    if (!listId) {
      console.warn("No Dashpivot list ID configured for locations");
      setLocations([]);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Fetch items from the configured list
      const listResponse = await getListById(listId);

      // Normalize existing names for case-insensitive comparison
      const normalizedExcluded = new Set(
        excludeNames.map((n) => n.trim().toLowerCase())
      );

      // Map list items to project locations
      const projectLocations: ProjectLocation[] = (
        listResponse.data.items || []
      )
        .map((item) => ({
          id: item.id,
          name: item.value,
          source: "dashpivot" as const,
        }))
        .filter((p) => !normalizedExcluded.has(p.name.trim().toLowerCase()))
        .sort((a, b) => a.name.localeCompare(b.name));

      setLocations(projectLocations);
    } catch (err) {
      logError("Error fetching project locations from Dashpivot:", err);
      setError(
        err instanceof Error ? err.message : "Failed to fetch locations"
      );
    } finally {
      setLoading(false);
    }
     
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isConfigured, JSON.stringify(excludeNames)]);

  useEffect(() => {
    fetchLocations();
  }, [fetchLocations]);

  return { locations, loading, error, isConfigured, refetch: fetchLocations };
}
